import React from 'react';
import clsx from 'clsx';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * The size of the input
   */
  size?: 'sm' | 'base' | 'lg' | 'table';
  
  /**
   * Whether the input should take the full width of its container
   */
  fullWidth?: boolean;
  
  /**
   * Whether the input is in an error state
   */
  error?: boolean;
  
  /**
   * Whether the input is in a success state
   */
  success?: boolean;
  
  /**
   * Icon to display at the start of the input
   */
  startIcon?: React.ReactNode;
  
  /**
   * Icon to display at the end of the input
   */
  endIcon?: React.ReactNode;
  
  /**
   * Label for the input
   */
  label?: string;
  
  /**
   * Helper text to display below the input
   */
  helperText?: string;
}

/**
 * Input component with consistent styling matching the Tailwind design
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'base',
      fullWidth = false,
      error = false,
      success = false,
      startIcon,
      endIcon,
      label,
      helperText,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    const inputId = React.useId();
    
    return (
      <div
        className={clsx(
          'prometheus-input-wrapper',
          {
            'prometheus-input-wrapper--full-width': fullWidth,
            'prometheus-input-wrapper--disabled': disabled,
            'prometheus-input-wrapper--error': error,
            'prometheus-input-wrapper--success': success,
          }
        )}
      >
        {label && (
          <label
            htmlFor={inputId}
            className="prometheus-input__label"
          >
            {label}
          </label>
        )}
        
        <div className="prometheus-input__container">
          {startIcon && (
            <span className="prometheus-input__start-icon" aria-hidden="true">
              {startIcon}
            </span>
          )}
          
          <input
            ref={ref}
            id={inputId}
            className={clsx(
              'prometheus-input',
              `prometheus-input--${size}`,
              {
                'prometheus-input--has-start-icon': startIcon,
                'prometheus-input--has-end-icon': endIcon,
                'prometheus-input--error': error,
                'prometheus-input--success': success,
                'prometheus-input--disabled': disabled,
              },
              className
            )}
            disabled={disabled}
            {...props}
          />
          
          {endIcon && (
            <span className="prometheus-input__end-icon" aria-hidden="true">
              {endIcon}
            </span>
          )}
        </div>
        
        {helperText && (
          <span
            className={clsx(
              'prometheus-input__helper-text',
              {
                'prometheus-input__helper-text--error': error,
                'prometheus-input__helper-text--success': success,
              }
            )}
          >
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;